import React, { Fragment, useRef, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { EditSoilAndLandVM } from './EditSoilAndLandVM';
import './editSoilAndLand.scss';
import { Table } from 'reactstrap';
import { DelineateWatershedInputStateVM } from '../DelineateWatershedInput/DelineateWatershedInputStateVM';
import { useRerender } from 'cd-eft/hooks/useRerender';
import { ToolSlot } from 'cd-eft/components/Design/BreaklineTools/framework/Tool';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { SplitLandUsePolygonsTool } from './EFT-851/SplitLandUsePolygonsTool';
import { TableView } from './EFT-851/TableView';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { PopOver } from 'cd-eft/components/common/PopOver/PopOver';
import { mdiInformationOutline } from '@mdi/js';
import { isObservable, observable } from 'mobx';
import { ToolBarLoadingSpinner } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { TooltipFromElement } from 'cd-eft/components/common/TooltipFromElement/TooltipFromElement'; 

interface IEditSoilAndLandProps {
  soilArray: Array<any>;
  landArray: Array<any>;
  callback: (soil: [], land: [], vm: any) => void;
  view: any;
  onCheckboxRowChanged: (data: any, layerType: number) => void;
  landUseTableComplete: boolean;
  currentStepIndex: number;
  activityViewModel: DelineateWatershedInputStateVM;
}

const EditSoilAndLandComponent = (props: IEditSoilAndLandProps) => {
  const rerender = useRerender();
  const [forceRender, setForceRender] = useState(0);
  const activityViewModel = props.activityViewModel;

  const vmRef = useRef<EditSoilAndLandVM>();
  if (!vmRef.current) {
    vmRef.current = new EditSoilAndLandVM(props, setForceRender);
  }
  const vm = vmRef.current;

  const { landUseTableComplete, currentStepIndex } = props;

  useEffect(() => {
    // set table state with props
    vm.initTables(props.soilArray, props.landArray);
    props.callback(vm.soilTable, vm.landUseTable, vm);
  }, []);

  const renderSoilOptions = (item, idx) => {
    return (
      <Fragment>
        <select
          className={'usa-select' + vm.errorClass('HYDGROUP', item.attributes.HYDGROUP)}
          // want to refactor this to be dynamic
          name="HYDGROUP"
          id={'soil-options' + idx}
          onChange={(event) => {
            vm.handleInputChange(event, idx, vm.soilTable);
          }}
          value={item.attributes.HYDGROUP || ''}
        >
          <option value=""> - select - </option>
          {vm.getHsgData(item.attributes.MUKEY, item.attributes.NO_A_HSG)
            ? vm.getHsgData(item.attributes.MUKEY, item.attributes.NO_A_HSG).map((el) => (
              <option value={el.hsg} key={el.hsg + idx}>
                {`${el.hsg}`}
              </option>
            ))
            : null}
        </select>
      </Fragment>
    );
  };

  const renderButtonAndPopOver = (item, i) => {
    return (
      <Fragment>
        {vm.getHsgDetailsData(item.attributes.MUKEY) ? (
          <span>
            View Details
            <TooltipFromElement
              key={i}
              id={i}
              icon={mdiInformationOutline}
              tooltipSide='right'
              extraClass='static-tooltip-from-element'
            >
              {vm.getHsgDetailsData(item.attributes.MUKEY).map((el) => {
                return el.pct > 0 ? (
                  <ul className='m-0'>
                    {el.hsg}: {el.pct}%
                  </ul>
                ) : null;
              })}
            </TooltipFromElement>
          </span>
        ) : 'No Data'}
      </Fragment>
    );
  };

  const renderDualHsgCheckmark = (item, i) => {
    let filteredArr = [];
    if (vm.getHsgDetailsData(item.attributes.MUKEY)) {
      filteredArr = vm.getHsgDetailsData(item.attributes.MUKEY).filter((el) => {
        return (el.hsg === 'A/D' || el.hsg === 'B/D' || el.hsg === 'C/D') && el.pct > 0;
      });
    }

    return <Fragment>{filteredArr.length ? <Fragment>&#10003;</Fragment> : null}</Fragment>;
  };

  const landUseOptions = vm.landUseOptions.map((item) => {
    return { name: item, value: item };
  }); 

  return (
    <div className="table-container">
      {/* Land Use Table */}
      {currentStepIndex === 3 && !landUseTableComplete ? (
        <Fragment>
          {!vm.loadingSalResults ? <Fragment>
            <div className="lulc-button-div">
              <h3>Modify Polygons</h3>
              <button
                className="usa-button"
                onClick={async () => {
                  if (activityViewModel.edit_SAL_LANDUSE_graphicsLayerView) {
                    const mapView = activityViewModel.mapView;
                    const toolSlot = new ToolSlot();
                    const landUsePolygonsLayer = activityViewModel.edit_SAL_LANDUSE_graphicsLayerView
                      ?.layer as GraphicsLayer;
                    const tool = new SplitLandUsePolygonsTool(mapView, toolSlot, landUsePolygonsLayer, activityViewModel.resetHydrologyValidationError);
                    await tool.activate();

                    rerender();
                  }
                }}
              >
                Split
              </button>
              <button
                className="usa-button"
                onClick={() => {
                  activityViewModel.mergeSelectedLandusePolygons();
                  rerender();
                }}
              >
                Merge
              </button>
              <button
                className="usa-button"
                onClick={() => {
                  MessageCenter.addWarning(
                    'Reset All will remove all edits and reset to the system generated Land Use polygons. Do you wish to continue?',
                    [
                      {
                        callback: () => {
                          MessageCenter.clearMessages();
                        },
                        text: 'Close',
                      },
                      {
                        callback: () => {
                          activityViewModel.resetLandusePolygons();
                          rerender();
                          MessageCenter.clearMessages();
                        },
                        text: 'Yes',
                      },
                    ]
                  );
                }}
              >
                Reset All
              </button>
            </div>

            {activityViewModel.edit_SAL_LANDUSE_graphicsLayerView !== undefined && (
              <Fragment>
                <TableView
                  fields={[
                    { caption: '#', property: 'OBJECTID' },
                    {
                      caption: 'Land Use',
                      property: 'LANDUSE',
                      canEdit: true,
                      inputType: 'select',
                      selectOptions: landUseOptions,
                      validationCallback: vm.checkValues,
                      validator: vm.landUseValidator,
                    },
                    {
                      caption: 'Condition',
                      property: 'CONDITION',
                      canEdit: true,
                      inputType: 'linked-select',

                      linkedProperty: 'LANDUSE',
                      linkedValueOptionsSet: vm.lulcLinkedValueOptions,
                      validationCallback: vm.checkValues,
                      validator: vm.landUseConditionValidator,
                      nestedOptions: true,
                    },
                    {
                      caption: 'Acres',
                      property: 'Acres',
                      canEdit: false,
                      formatter: (value: number) => {
                        return value.toFixed(2);
                      },
                    },
                    {
                      caption: 'Percent', 
                      property: 'Acres', 
                      canEdit: false, 
                      formatter: (value: number) => {
                        return (((value as number) / ((vm.props.activityViewModel.watershedAcreage) as number) * 100).toFixed(2) + '%'); 
                      }, 
                    }, 
                  ]}
                  rowsSource={(activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer as GraphicsLayer).graphics
                    .toArray()
                    .map((graphic, index) => {
                      // convert the attributes object to mobx observable (this make things much easier!)
                      if (!isObservable(graphic.attributes)) {
                        graphic.attributes = observable(graphic.attributes);
                      }
                      return graphic.attributes;
                    })}
                  onMouseEnterRow={(e) => {
                    if (activityViewModel.edit_SAL_LANDUSE_graphicsLayerView) {
                      const attributes = e.props.rowSource;

                      let handle = attributes.__highlight1;
                      if (handle) handle.remove();
                      attributes.__highlight1 = handle;

                      const graphics = (activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer as GraphicsLayer)
                        .graphics;
                      const graphic = graphics.find((graphic) => {
                        return graphic.attributes === attributes;
                      });
                      handle = activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.highlight(graphic);
                      attributes.__highlight1 = handle;
                    }
                  }}
                  onMouseLeaveRow={(e) => {
                    const attributes = e.props.rowSource;
                    let handle = attributes.__highlight1;
                    if (handle) handle.remove();
                    attributes.__highlight1 = undefined;
                  }}
                  selectionMode="multiple"
                  selectedRows={activityViewModel.selectedLandUse}
                  onSelectedRowsChanged={(e) => {
                    activityViewModel.syncLandUseSelection();
                  }}
                />
              </Fragment>
            )}
          </Fragment> : <ToolBarLoadingSpinner message="Loading Land Use Land Cover Data..." jobExecuting={vm.loadingSalResults} />}
        </Fragment>
      ) : null}
      {/* Land Use Table End */}

      {/* HSG Table */}
      {currentStepIndex === 3 && landUseTableComplete ? (
        <Fragment>
          {!vm.loadingSalResults ? <Table className="table-sm">
            <thead className="table-head">
              <tr>
                <th className="p-3">
                  <input
                    className="usa-checkbox__input"
                    data-behavior="select-table-row"
                    id="selectAllSoil"
                    data-testid="selectAllSoil"
                    type="checkbox"
                    name="selectAllSoil"
                    value="selectAllSoil"
                    checked={vm.selectAllSoilItems}
                    onChange={vm.onAllSoilsRowCheckChanged}
                  />
                  <label className="usa-checkbox__label" htmlFor="selectAllSoil"></label>
                </th>
                <th className="table-headers">Object ID</th>
                <th className="table-headers">HSG</th>
                <th className="table-headers">HSG Details</th>
                <th className="table-headers">Dual HSG</th>
                <th className="table-headers">Acres</th>
                <th className="table-headers">Percent</th>
              </tr>
            </thead>
            <tbody className="land-and-soil-table">
              {vm.soilTable
                .filter((el) => el !== undefined)
                .map((item, i) => {
                  return (
                    <tr
                      id={'soil-table-row-' + item.attributes['OBJECTID']}
                      key={'key' + i}
                      onMouseEnter={(e) => vm.handleSoilRowMouseEnter(e, i)}
                      onMouseLeave={(e) => vm.handleSoilRowMouseLeave(e, i)}
                    >
                      <td>
                        <div className="input-container">
                          <input
                            className="usa-checkbox__input"
                            data-behavior="select-table-row"
                            id={'select' + i}
                            data-testid={'select' + i}
                            type="checkbox"
                            name={item.attributes.OBJECTID}
                            value="select"
                            checked={vm.checkedSoils.includes(item.attributes.OBJECTID)}
                            onChange={vm.onSoilsRowCheckChanged}
                          />
                          <label className="usa-checkbox__label" htmlFor={'select' + i}></label>
                        </div>
                      </td>

                      <td className="p-3">{item.attributes.OBJECTID}</td>
                      <td className="hsg-options">
                        <form className="usa-form pt-1">{renderSoilOptions(item, i)}</form>
                      </td>
                      <td className="p-3">{renderButtonAndPopOver(item, i)}</td>
                      <td className="p-3">{renderDualHsgCheckmark(item, i)}</td>
                      <td className="p-3">{item.attributes.Acres?.toFixed(2)}</td>
                      <td className="p-3">{((item.attributes.Acres / vm.props.activityViewModel.watershedAcreage) * 100).toFixed(2)}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </Table> : <ToolBarLoadingSpinner message="Loading Soil Data..." jobExecuting={vm.loadingSalResults} />}
        </Fragment>
      ) : null}
      {/* HSG Table End */}
    </div>
  );
};

export const EditSoilAndLand = observer(EditSoilAndLandComponent);
